import "./NavBar.css";

import { Button } from "../buttons/Button";
import { ReactComponent as FolderSVG } from "../../assets/icons/folder-title.svg";
import { NavBarAppMenu } from "./nav-bar-app-menu/NavBarAppMenu";
import { NavBarUserMenu } from "./nav-bar-user-menu/NavBarUserMenu";
import { orbitalWatchAccountUrl, orbitalWatchEnvironment } from "./orbitalWatchApps";
import { ConfigurationProvider } from "../../infrastructure/configuration/ConfigurationProvider";
import { ReactComponent as NavbarIconSVG } from "../../assets/icons/navbar-icon.svg";
import { useIdentityUserDisplayName } from "../../infrastructure/identity/hooks/useIdentityUserDisplayName";
import { useNavigate } from "react-router-dom";
import { useSelectedProject } from "../../infrastructure/ui/UIServices";
import { useCallback, useMemo, useRef, useState } from "react";
import { useOutsideComponentClickTrigger } from "../../hooks/useOutsideComponentClickTrigger";

export function NavBar() {
  const navigate = useNavigate();

  const selectedProject = useSelectedProject();
  const [showMenu, setShowMenu] = useState(false);
  const [showUserMenu, setShowUserMenu] = useState(false);
  const {userDisplayName, userDisplayEmail, userDisplayNameIsLoading} = useIdentityUserDisplayName();

  // The hook treats everything outside the ref as "outside", and each trigger sits next to its menu
  // rather than inside it — so the ref has to span both, or a click on the trigger would close the
  // menu on mousedown and the trigger's own onClick would immediately reopen it.
  const appMenuRef = useRef<HTMLDivElement>(null);
  const userMenuRef = useRef<HTMLDivElement>(null);
  useOutsideComponentClickTrigger(appMenuRef, useCallback(() => setShowMenu(false), []));
  useOutsideComponentClickTrigger(userMenuRef, useCallback(() => setShowUserMenu(false), []));

  // Up to two initials from the display name, falling back to the email, so the avatar still reads
  // as something while the silent token call is in flight or the name claim is missing.
  const initials = useMemo(() => {
    const source = userDisplayName || userDisplayEmail || "";
    const parts = source.split(/[\s.@_-]+/).filter(Boolean);
    return parts.slice(0, 2).map((w) => w[0]).join("").toUpperCase() || "?";
  }, [userDisplayName, userDisplayEmail]);

  return (
    <div className="nav-bar header">
      <div className="nav-bar-menu-anchor" ref={appMenuRef}>
        <div className="header-menu-btn" onClick={() => {setShowMenu(!showMenu)}}> <NavbarIconSVG /></div>
        {showMenu && <NavBarAppMenu onClose={() => setShowMenu(false)} />}
      </div>
      <div className="nav-bar-app-logo"  onClick={() => navigate('/projects')}>Aurora</div>
      <div className="nav-bar-space"></div>
      <div className="nav-bar-project"> {selectedProject ? <><FolderSVG></FolderSVG> {selectedProject.name}</> : null}</div>
      <div className="nav-bar-menu-anchor" ref={userMenuRef}>
      <div
        className="nav-bar-user-actions"
        onClick={() => setShowUserMenu((open) => !open)}
        role="button"
        tabIndex={0}
        aria-haspopup="menu"
        aria-expanded={showUserMenu}
        onKeyDown={(ev) => { if (ev.key === "Enter" || ev.key === " ") setShowUserMenu((open) => !open); }}
      >
        {userDisplayNameIsLoading ? null : (
          <>
            <span className="nav-bar-user-avatar">{initials}</span>
            <span className="nav-bar-user-name">{userDisplayName}</span>
          </>
        )}
      </div>
      {showUserMenu && (
        <NavBarUserMenu
          displayName={userDisplayName}
          email={userDisplayEmail}
          // "View account": the Identity app's profile page on this environment (QA / prod).
          accountUrl={orbitalWatchAccountUrl(orbitalWatchEnvironment(ConfigurationProvider.getConfiguration()?.App?.BackendUrl))}
          onClose={() => setShowUserMenu(false)}
        />
      )}
      </div>
    </div>
  );
}
